import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { doc, updateDoc, getDoc } from 'firebase/firestore';
import { db, auth } from '../firebase';

const OnboardingStep7 = () => {
  const navigate = useNavigate();
  const [mostWantedPedals, setMostWantedPedals] = useState([]);
  const [loading, setLoading] = useState(true);

  // Scroll to top when component mounts
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  useEffect(() => {
    const loadMostWantedPedals = async () => {
      try {
        // Try to load from Firestore stats document
        const statsDoc = await getDoc(doc(db, 'stats', 'mostWantedPedals'));
        
        if (statsDoc.exists() && statsDoc.data().pedals) {
          setMostWantedPedals(statsDoc.data().pedals.slice(0, 10));
        } else {
          // If stats don't exist yet, show placeholder
          setMostWantedPedals([]);
        }
      } catch (error) {
        console.error('Error loading most wanted pedals:', error);
        setMostWantedPedals([]);
      } finally {
        setLoading(false);
      }
    };

    loadMostWantedPedals();
  }, []);

  const handleCompleteSignUp = async () => {
    try {
      const user = auth.currentUser;
      if (!user) {
        console.error('No authenticated user');
        return;
      }

      // Mark step 7 as complete (onboardingComplete is already set in Step 5)
      await updateDoc(doc(db, 'users', user.uid), {
        step7Complete: true
      });

      // Navigate to latest-pedals page
      navigate('/latest-pedals');
      
      // Scroll to top after navigation
      window.scrollTo(0, 0);
    } catch (error) {
      console.error('Error completing sign up:', error);
    }
  };

  return (
    <>
      <style>{`
        @media (max-width: 1024px) {
          body {
            padding-bottom: 0 !important;
          }
          .onboarding-container {
            min-height: calc(100vh - var(--navbar-height, 125px)) !important;
            padding-bottom: 120px !important;
          }
        }
      `}</style>
      <div className="onboarding-container" style={{ 
        minHeight: '100vh', 
        backgroundColor: '#f5f5f5', 
        padding: '2rem 1rem',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center'
      }}>
      <div style={{ 
        maxWidth: '1000px', 
        width: '100%',
        backgroundColor: 'white',
        borderRadius: '12px',
        padding: '3rem 2rem',
        boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
        boxSizing: 'border-box'
      }}
      className="onboarding-step7-container">
        {/* Header */}
        <h1 style={{
          fontFamily: 'Nunito, sans-serif',
          fontSize: '1.875rem',
          fontWeight: '700',
          color: '#4A5759',
          textAlign: 'center',
          marginBottom: '2rem',
          lineHeight: '1.4'
        }}>
          Great! We'll start watching for those pedals.
        </h1>

        {/* Subtext */}
        <p style={{
          fontFamily: 'Nunito, sans-serif',
          fontSize: '1.125rem',
          color: '#6b7280',
          textAlign: 'center',
          marginBottom: '2.5rem',
          lineHeight: '1.6'
        }}>
          These are the 10 most wanted pedals on PXC. Have one? List it today and the people who want it will find you.
        </p>

        {/* Most Wanted Pedals Grid */}
        {loading ? (
          <div style={{
            textAlign: 'center',
            padding: '3rem',
            fontFamily: 'Nunito, sans-serif',
            color: '#6b7280'
          }}>
            Loading most wanted pedals...
          </div>
        ) : mostWantedPedals.length > 0 ? (
          <div style={{ display: 'flex', justifyContent: 'center', marginBottom: '3rem' }}>
            <div style={{
              display: 'grid',
              gridTemplateColumns: 'repeat(5, 1fr)',
              gap: '1rem'
            }}
            className="most-wanted-grid">
            {mostWantedPedals.map((pedal, index) => (
              <div
                key={index}
                style={{
                  border: '2px solid #e5e7eb',
                  borderRadius: '8px',
                  padding: '1.25rem 1rem',
                  textAlign: 'center',
                  backgroundColor: '#fafafa',
                  minHeight: '100px',
                  display: 'flex',
                  flexDirection: 'column',
                  justifyContent: 'center'
                }}
              >
                <div style={{
                  fontFamily: 'Nunito, sans-serif',
                  fontSize: '1rem',
                  fontWeight: '700',
                  color: '#4A5759',
                  marginBottom: '0.25rem',
                  lineHeight: '1.3'
                }}>
                  {pedal.make}
                </div>
                <div style={{
                  fontFamily: 'Nunito, sans-serif',
                  fontSize: '0.875rem',
                  color: '#6b7280',
                  lineHeight: '1.3'
                }}>
                  {pedal.model}
                </div>
              </div>
            ))}
            </div>
          </div>
        ) : (
          <div style={{
            textAlign: 'center',
            padding: '3rem',
            backgroundColor: '#f9fafb',
            borderRadius: '8px',
            marginBottom: '3rem',
            fontFamily: 'Nunito, sans-serif',
            color: '#6b7280'
          }}>
            Most wanted pedals are being calculated. Check back soon!
          </div>
        )}

        {/* Text above button */}
        <p style={{
          fontFamily: 'Nunito, sans-serif',
          fontSize: '1.125rem',
          color: '#6b7280',
          textAlign: 'center',
          marginTop: '3rem',
          marginBottom: '1.5rem',
          lineHeight: '1.6',
          fontWeight: 'normal'
        }}>
          The last step is to list a pedal on your Pedalboard. Once it's live, you're visible to every trader on PXC.
        </p>

        {/* Complete Sign Up Button */}
        <div style={{ textAlign: 'center' }}>
          <button
            onClick={handleCompleteSignUp}
            style={{
              backgroundColor: '#C9A227',
              color: 'white',
              padding: '0.875rem 3rem',
              border: 'none',
              borderRadius: '9999px',
              fontSize: '1.125rem',
              fontWeight: 'bold',
              fontFamily: 'Nunito, sans-serif',
              cursor: 'pointer',
              transition: 'all 0.3s ease',
              boxShadow: '0 2px 8px rgba(201, 162, 39, 0.3)'
            }}
            onMouseEnter={(e) => {
              e.target.style.backgroundColor = '#b8912a';
              e.target.style.transform = 'translateY(-2px)';
              e.target.style.boxShadow = '0 4px 12px rgba(201, 162, 39, 0.4)';
            }}
            onMouseLeave={(e) => {
              e.target.style.backgroundColor = '#C9A227';
              e.target.style.transform = 'translateY(0)';
              e.target.style.boxShadow = '0 2px 8px rgba(201, 162, 39, 0.3)';
            }}
          >
            Complete Sign Up
          </button>
        </div>

        {/* Add responsive CSS for mobile */}
        <style>
          {`
            @media (max-width: 768px) {
              .onboarding-step7-container {
                padding: 2rem 0.75rem !important;
              }
              .most-wanted-grid {
                grid-template-columns: repeat(2, 1fr) !important;
                width: fit-content !important;
                margin-left: auto !important;
                margin-right: auto !important;
              }
              body {
                overflow-x: hidden;
              }
            }
          `}
        </style>
      </div>
    </div>
    </>
  );
};

export default OnboardingStep7;

